import { useState, useCallback, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Plus, Replace, X } from 'lucide-react'
import { vocabularyApi } from '@/api/vocabulary'
import { reviewApi, type ReviewCard, type ReviewMode } from '@/api/review'
import { WordPickerPanel } from '@/components/vocabulary/WordPickerPanel'
import { Button } from '@/components/ui/Button'
import { cn } from '@/components/ui/cn'
import { userWordToReviewCard } from '@/lib/reviewCard'
import { mergeWordAndPhraseRows } from '@/lib/mergeVocabularyUnits'
import { loadVocabularySort, saveVocabularySort, type VocabularySortMode } from '@/lib/vocabularySortStorage'

type AddMode = 'append' | 'replace'

interface AddWordsToReviewDialogProps {
  open: boolean
  onClose: () => void
  onAdd: (cards: ReviewCard[], mode: AddMode) => void
  existingWordIds: Set<string>
  hasExistingCards?: boolean
  reviewMode?: ReviewMode
  title?: string
}

export function AddWordsToReviewDialog({
  open,
  onClose,
  onAdd,
  existingWordIds,
  hasExistingCards = false,
  reviewMode = 'CLASSIC',
  title = 'Add vocabulary to session',
}: AddWordsToReviewDialogProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [addMode, setAddMode] = useState<AddMode>('append')
  const [submitting, setSubmitting] = useState(false)
  const [sortMode, setSortMode] = useState<VocabularySortMode>(() => loadVocabularySort())
  const [collectionId, setCollectionId] = useState<string | undefined>()

  const wordListFilters = useMemo(
    () => ({
      size: 200,
      sortBy: sortMode === 'dateAdded' ? ('DATE_ADDED' as const) : ('ALPHABETICAL' as const),
      collectionId,
    }),
    [sortMode, collectionId],
  )

  const handleSortModeChange = useCallback((mode: VocabularySortMode) => {
    setSortMode(mode)
    saveVocabularySort(mode)
  }, [])

  const { data: wordsPage, isLoading } = useQuery({
    queryKey: ['vocabulary', wordListFilters],
    queryFn: () => vocabularyApi.getWords(wordListFilters).then((r) => r.data),
    enabled: open,
  })

  const { data: phrasesPage } = useQuery({
    queryKey: ['vocabularyPhrases', wordListFilters],
    queryFn: () => vocabularyApi.getPhrases({ page: 0, size: 200 }).then((r) => r.data),
    enabled: open && !collectionId,
  })

  const words = useMemo(
    () => mergeWordAndPhraseRows(wordsPage?.content ?? [], phrasesPage?.content ?? []),
    [wordsPage?.content, phrasesPage?.content],
  )
  const toggleWord = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const selectMany = (ids: string[], selected: boolean) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      for (const id of ids) {
        if (selected) next.add(id)
        else next.delete(id)
      }
      return next
    })
  }

  const handleCollectionChange = useCallback((id: string | undefined) => {
    setCollectionId(id)
    setSelectedIds(new Set())
  }, [])

  const resetForm = () => {
    setSelectedIds(new Set())
    setSearchQuery('')
    setAddMode('append')
    setSubmitting(false)
    setCollectionId(undefined)
  }

  const handleConfirm = async () => {
    if (selectedIds.size === 0 || submitting) return

    const ids = [...selectedIds]
    setSubmitting(true)
    try {
      let newCards: ReviewCard[]
      try {
        newCards = await reviewApi.getDueCards(ids.length, reviewMode, ids)
      } catch {
        const selectedWords = words.filter((w) => selectedIds.has(w.id))
        newCards = selectedWords.map(userWordToReviewCard)
      }

      if (addMode === 'append') {
        newCards = newCards.filter((card) => !existingWordIds.has(card.wordId))
      }

      if (newCards.length === 0) {
        return
      }

      onAdd(newCards, addMode)
      resetForm()
      onClose()
    } finally {
      setSubmitting(false)
    }
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  if (!open) return null

  const confirmLabel = addMode === 'replace' ? 'Replace queue' : 'Add to session'
  const showModeChoice = hasExistingCards

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative flex max-h-[85vh] w-full max-w-lg flex-col rounded-t-2xl border bg-background shadow-xl sm:rounded-2xl">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h2 className="font-semibold">{title}</h2>
          <button type="button" onClick={handleClose} className="text-muted-foreground hover:text-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        {showModeChoice && (
          <div className="space-y-2 border-b px-4 py-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Queue action</p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setAddMode('append')}
                className={cn(
                  'rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                  addMode === 'append'
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:bg-accent/50',
                )}
                data-testid="review-add-mode-append"
              >
                <span className="flex items-center gap-1.5 font-medium">
                  <Plus className="h-3.5 w-3.5" />
                  Add to queue
                </span>
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  Keep current cards and add selected
                </span>
              </button>
              <button
                type="button"
                onClick={() => setAddMode('replace')}
                className={cn(
                  'rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                  addMode === 'replace'
                    ? 'border-primary bg-primary/10'
                    : 'border-border hover:bg-accent/50',
                )}
                data-testid="review-add-mode-replace"
              >
                <span className="flex items-center gap-1.5 font-medium">
                  <Replace className="h-3.5 w-3.5" />
                  Replace queue
                </span>
                <span className="mt-0.5 block text-xs text-muted-foreground">
                  Start fresh with selected words only
                </span>
              </button>
            </div>
          </div>
        )}

        <WordPickerPanel
          words={words}
          selectedIds={selectedIds}
          onToggle={toggleWord}
          onSelectMany={selectMany}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          loading={isLoading}
          showSortToggle
          sortMode={sortMode}
          onSortModeChange={handleSortModeChange}
          showCollectionFilter
          collectionId={collectionId}
          onCollectionChange={handleCollectionChange}
          className="min-h-[320px] max-h-[50vh]"
        />

        <div className="flex items-center justify-between gap-3 border-t px-4 py-3">
          <p className="text-sm text-muted-foreground">
            {selectedIds.size} selected
          </p>
          <div className="flex gap-2">
            <Button
              type="button"
              className="h-9 w-auto bg-secondary px-4 text-secondary-foreground hover:bg-secondary/80"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              className="h-9 w-auto gap-1.5 px-4"
              disabled={selectedIds.size === 0 || submitting}
              loading={submitting}
              onClick={() => void handleConfirm()}
              data-testid="review-add-words-confirm"
            >
              {!submitting && (addMode === 'replace' ? <Replace className="h-4 w-4" /> : <Plus className="h-4 w-4" />)}
              {confirmLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
